import React from 'react';
import { Security, Code, AccountTree, Description, BugReport } from '@mui/icons-material';
import type { Scan, Issue } from '../../store/api/githubApi';

export const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];

export const SEVERITY_COLOR: Record<string, 'error' | 'warning' | 'info' | 'default' | 'success'> = {
  critical: 'error', high: 'error', medium: 'warning', low: 'info', info: 'default',
};

export const SEVERITY_HEX: Record<string, string> = {
  critical: '#d32f2f', high: '#ef5350', medium: '#ff9800', low: '#29b6f6', info: '#9e9e9e',
};

// score_security and score_dependencies are now the SAME combined value
// (code-level findings + dependency CVEs scored as one pool — see the
// backend's scan_scorer.py compute_debt_score() docstring, fixed 2026-09-18
// after a customer found a repo showing "Security: 100" while carrying
// $900K in dependency CVE exposure). The two cards below still filter to
// genuinely different issue lists (issueKey 'security' vs 'dependencies'
// are real, distinct findings), so both stay — only "Vulnerabilities"
// hides its own grade/score display (hideScore) so the two cards can't
// show the identical score alongside DIFFERENT issue counts, which would
// read as a bug rather than the intentional shared-score design it is.
export const CATEGORIES: { key: keyof Scan; label: string; icon: React.ElementType; issueKey: string; na?: boolean; hideScore?: boolean }[] = [
  { key: 'score_security',      label: 'Security',      icon: Security,     issueKey: 'security' },
  { key: 'score_complexity',    label: 'Complexity',    icon: Code,         issueKey: 'complexity' },
  { key: 'score_dependencies',  label: 'Vulnerabilities', icon: AccountTree,  issueKey: 'dependencies', hideScore: true },
  { key: 'score_documentation', label: 'Documentation', icon: Description,  issueKey: 'documentation' },
  { key: 'score_coverage',      label: 'Coverage',      icon: BugReport,    issueKey: 'coverage', na: true },
];

export const FIXABLE_COMPLEXITIES = new Set(['trivial', 'simple', 'moderate']);

// AI-assisted fixing is backed by a real sandboxed verification engine
// (backend docs/fix-verification-design.md) only for Python and Java — the
// same extension-based language check the backend's own scan pipeline uses
// to route files to per-language tools (app/workers/tool_runners.py), so
// this stays in sync with the backend without needing a dedicated
// `language` field on Issue. Deliberately excludes .jsp even though the
// backend's Java scanner covers it — the Java verification sandbox only
// compile-checks .java files (see verify_java_entrypoint.py), so offering
// an unverified "AI Fix" for .jsp would be a worse experience than showing
// "Manual remediation required".
const FIXABLE_FILE_EXTENSIONS = new Set(['.py', '.java']);

function hasFixableExtension(filePath: string): boolean {
  const dot = filePath.lastIndexOf('.');
  return dot !== -1 && FIXABLE_FILE_EXTENSIONS.has(filePath.slice(dot).toLowerCase());
}

// Single source of truth for "can this issue show the AI Fix option" —
// combines the existing complexity gate with the language gate above. Use
// this everywhere instead of checking FIXABLE_COMPLEXITIES directly, so the
// two conditions can never drift out of sync across call sites.
export function isIssueFixable(issue: Pick<Issue, 'ai_fix_complexity' | 'file_path'>): boolean {
  return FIXABLE_COMPLEXITIES.has(issue.ai_fix_complexity ?? '') && hasFixableExtension(issue.file_path);
}

export const DONUT_COLORS: Record<string, string> = {
  critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#60a5fa', info: '#d1d5db',
};
